/**
 * Light, dark, or whatever the device says.
 *
 * The stylesheet was already written for all three: `:root` carries the
 * light tokens, `@media (prefers-color-scheme: dark)` applies the dark ones
 * unless `[data-theme="light"]` opts out, and `[data-theme="dark"]` forces
 * them on. Nothing ever set that attribute, so the only reachable state was
 * "follow the device". This supplies the missing half.
 *
 * Three states rather than two on purpose. A plain on/off switch has to
 * pick a side the first time someone loads the app, which means either
 * ignoring their system preference or pretending they chose something they
 * did not. "System" is the honest default and stays correct when they
 * change their phone to dark at sunset.
 */

export type Theme = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "ledger.theme";

export function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? systemTheme() : theme;
}

/** What the user last chose, or "system" if they never have. */
export function readTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Private browsing, blocked storage. Following the device is a fine
    // answer and not worth an error for.
  }
  return "system";
}

/**
 * Write the choice to the document.
 *
 * "system" removes the attribute rather than setting it to anything, which
 * hands control back to the media query - that is what the stylesheet's
 * `:not([data-theme="light"])` is waiting for.
 */
export function applyTheme(theme: Theme): ResolvedTheme {
  const root = document.documentElement;
  if (theme === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
  return resolveTheme(theme);
}

export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The choice holds for this session even if it cannot be remembered.
  }
}

/**
 * Apply the stored choice as early as possible.
 *
 * Called from an inline script before React mounts, so the first painted
 * frame is already the right colour. Doing it in a component means a white
 * flash on every load for anyone using dark.
 */
export function initTheme(): ResolvedTheme {
  return applyTheme(readTheme());
}

/** Tells the caller when the device preference changes, while on "system". */
export function watchSystemTheme(onChange: (theme: ResolvedTheme) => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => onChange(query.matches ? "dark" : "light");
  query.addEventListener("change", handler);
  return () => query.removeEventListener("change", handler);
}
